<template>
  <div class="flex h-screen bg-[#F8FAFC] font-sans text-slate-900 overflow-hidden">
    <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">

    <!-- Sidebar Gauche -->
    <aside class="w-72 bg-white border-r border-slate-200 flex flex-col z-20 shadow-sm">
      <div class="p-6 border-b border-slate-100">
        <h1 class="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Ticket Designer</h1>
        <p class="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Impression Thermique 58mm</p>
      </div>

      <LanguageTabs
        v-model="activeLanguage"
        :languages="availableLanguages"
        :configured-languages="configuredLanguages"
        @copy-from="copyLanguageConfig"
      />

      <div class="flex-1 overflow-y-auto p-4 space-y-6">
        <!-- Sections -->
        <div>
          <h3 class="text-xs font-bold text-slate-400 uppercase mb-3 px-2">Sections</h3>
          <div class="space-y-2">
            <button @click="activeSection = 'header'"
                    :class="['w-full text-left p-3 rounded-xl transition-all', activeSection === 'header' ? 'bg-blue-50 border-blue-200 border-2' : 'bg-slate-50 border border-slate-100']">
              <div class="font-semibold text-sm">📄 En-tête</div>
            </button>
            <button @click="activeSection = 'body'"
                    :class="['w-full text-left p-3 rounded-xl transition-all', activeSection === 'body' ? 'bg-blue-50 border-blue-200 border-2' : 'bg-slate-50 border border-slate-100']">
              <div class="font-semibold text-sm">🎫 Corps</div>
            </button>
            <button @click="activeSection = 'footer'"
                    :class="['w-full text-left p-3 rounded-xl transition-all', activeSection === 'footer' ? 'bg-blue-50 border-blue-200 border-2' : 'bg-slate-50 border border-slate-100']">
              <div class="font-semibold text-sm">📋 Pied de page</div>
            </button>
          </div>
        </div>

        <!-- Format papier -->
        <div class="pt-4 border-t">
          <h3 class="text-xs font-bold text-slate-400 uppercase mb-3 px-2">Format</h3>
          <select v-model="config.dimensions.paperType" class="w-full p-2 bg-slate-50 rounded-lg border text-xs px-2">
            <option value="58mm">58mm (standard)</option>
            <option value="80mm">80mm (large)</option>
          </select>
        </div>
      </div>

      <div class="p-4 border-t border-slate-100 space-y-2">
        <!-- Boutons Import/Export -->
        <div class="flex gap-2">
          <button @click="exportConfig"
                  class="flex-1 py-2 bg-white border border-slate-200 rounded-lg hover:border-green-400 text-xs font-semibold text-slate-700 hover:text-green-600 transition-all">
            📥 Export
          </button>
          <label class="flex-1 py-2 bg-white border border-slate-200 rounded-lg hover:border-green-400 text-xs font-semibold text-slate-700 hover:text-green-600 transition-all cursor-pointer text-center">
            📤 Import
            <input type="file" accept=".json" @change="importConfig" class="hidden" />
          </label>
        </div>

        <button @click="saveConfig" class="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold shadow-xl hover:bg-blue-600 transition-all flex items-center justify-center gap-2">
          <span>🚀 Publier</span>
        </button>
      </div>
    </aside>

    <!-- Canvas Preview (58mm simulation) -->
    <main class="flex-1 relative bg-slate-100 overflow-hidden p-12 flex justify-center items-start overflow-y-auto">
      <div class="my-8">
        <div class="mb-4 text-center">
          <span class="text-xs font-bold text-slate-500 bg-white px-4 py-2 rounded-full shadow">Simulation {{ config.dimensions.paperType }}</span>
        </div>

        <div class="bg-white shadow-2xl mx-auto" :style="{ width: ticketWidth + 'mm' }">
          <!-- Ticket Content -->
          <div class="ticket-content font-mono text-black bg-white"
               :style="{
                 paddingTop: currentLanguageConfig.margins.top + 'mm',
                 paddingBottom: currentLanguageConfig.margins.bottom + 'mm',
                 paddingLeft: currentLanguageConfig.margins.left + 'mm',
                 paddingRight: currentLanguageConfig.margins.right + 'mm'
               }">

            <!-- HEADER -->
            <div v-if="currentLanguageConfig.header.show" class="header-section">
              <!-- Logo -->
              <div v-if="currentLanguageConfig.header.logo.show && currentLanguageConfig.header.logo.url"
                   :style="{ textAlign: currentLanguageConfig.header.logo.align, marginBottom: '3mm' }">
                <img :src="currentLanguageConfig.header.logo.url"
                     :style="{ width: currentLanguageConfig.header.logo.width + 'mm', height: 'auto' }"
                     style="display: inline-block" />
              </div>

              <!-- Header Text -->
              <div v-if="currentLanguageConfig.header.text.show"
                   :style="{
                     fontSize: currentLanguageConfig.header.text.fontSize + 'pt',
                     fontWeight: currentLanguageConfig.header.text.fontWeight,
                     textAlign: currentLanguageConfig.header.text.align,
                     marginTop: currentLanguageConfig.header.text.marginTop + 'mm',
                     marginBottom: currentLanguageConfig.header.text.marginBottom + 'mm'
                   }">
                {{ currentLanguageConfig.header.text.content }}
              </div>

              <!-- Header Separator -->
              <div v-if="currentLanguageConfig.header.separator.show"
                   :style="{
                     borderTop: '1px ' + currentLanguageConfig.header.separator.style + ' #000',
                     margin: '3mm 0'
                   }"></div>
            </div>

            <!-- BODY -->
            <div class="body-section">
              <!-- Label -->
              <div v-if="currentLanguageConfig.body.label.show"
                   :style="{
                     fontSize: currentLanguageConfig.body.label.fontSize + 'pt',
                     textAlign: currentLanguageConfig.body.label.align,
                     marginBottom: '2mm'
                   }">
                {{ currentLanguageConfig.body.label.text }}
              </div>

              <!-- Ticket Number -->
              <div :style="{
                     fontSize: currentLanguageConfig.body.ticketNumber.fontSize + 'pt',
                     fontWeight: currentLanguageConfig.body.ticketNumber.fontWeight,
                     textAlign: currentLanguageConfig.body.ticketNumber.align,
                     marginTop: currentLanguageConfig.body.ticketNumber.marginTop + 'mm',
                     marginBottom: currentLanguageConfig.body.ticketNumber.marginBottom + 'mm',
                     letterSpacing: '2px'
                   }">
                A042
              </div>

              <!-- Additional Info -->
              <div v-for="(info, idx) in sortedAdditionalInfo" :key="idx"
                   v-show="info.show"
                   :style="{
                     fontSize: info.fontSize + 'pt',
                     textAlign: info.align,
                     marginTop: '1mm'
                   }">
                <span v-if="info.label">{{ info.label }}: </span>
                <span v-if="info.type === 'date'">{{ new Date().toLocaleDateString() }}</span>
                <span v-else-if="info.type === 'time'">{{ new Date().toLocaleTimeString() }}</span>
                <span v-else-if="info.type === 'datetime'">{{ new Date().toLocaleString() }}</span>
                <span v-else-if="info.type === 'queue'">File A</span>
                <span v-else-if="info.type === 'counter'">Guichet 1</span>
                <span v-else-if="info.type === 'custom'">{{ info.customValue }}</span>
              </div>

              <!-- QR Code -->
              <div v-if="currentLanguageConfig.body.qrCode.show"
                   :style="{
                     textAlign: currentLanguageConfig.body.qrCode.align,
                     marginTop: currentLanguageConfig.body.qrCode.marginTop + 'mm'
                   }">
                <div class="inline-block bg-black"
                     :style="{
                       width: currentLanguageConfig.body.qrCode.size + 'mm',
                       height: currentLanguageConfig.body.qrCode.size + 'mm'
                     }">
                  <div class="w-full h-full flex items-center justify-center text-white text-[8px]">QR</div>
                </div>
              </div>
            </div>

            <!-- FOOTER -->
            <div v-if="currentLanguageConfig.footer.show" class="footer-section">
              <!-- Footer Separator -->
              <div v-if="currentLanguageConfig.footer.separator.show"
                   :style="{
                     borderTop: '1px ' + currentLanguageConfig.footer.separator.style + ' #000',
                     margin: '3mm 0'
                   }"></div>

              <!-- Footer Text -->
              <div :style="{
                     fontSize: currentLanguageConfig.footer.text.fontSize + 'pt',
                     textAlign: currentLanguageConfig.footer.text.align,
                     marginTop: currentLanguageConfig.footer.text.marginTop + 'mm',
                     marginBottom: currentLanguageConfig.footer.text.marginBottom + 'mm'
                   }">
                {{ currentLanguageConfig.footer.text.content }}
              </div>

              <!-- Contact -->
              <div v-if="currentLanguageConfig.footer.contact.show && currentLanguageConfig.footer.contact.lines.length"
                   :style="{
                     fontSize: currentLanguageConfig.footer.contact.fontSize + 'pt',
                     textAlign: currentLanguageConfig.footer.contact.align
                   }">
                <div v-for="(line, idx) in currentLanguageConfig.footer.contact.lines" :key="idx">
                  {{ line }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Bouton imprimer -->
        <div class="text-center mt-6">
          <button @click="printTicket" class="px-6 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-all shadow-lg">
            🖨️ Tester l'impression
          </button>
        </div>
      </div>
    </main>

    <!-- Modal de sélection de kiosque -->
    <div v-if="showKioskModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div class="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <h3 class="text-2xl font-bold mb-4 text-slate-900">Choisir un kiosque</h3>
        <p class="text-sm text-slate-600 mb-6">Sélectionnez le kiosque sur lequel tester l'impression</p>

        <div class="space-y-3 mb-6 max-h-96 overflow-y-auto">
          <button
            v-for="kiosk in availableKiosks"
            :key="kiosk._id"
            @click="selectKiosk(kiosk)"
            class="w-full p-4 bg-slate-50 hover:bg-blue-50 border-2 border-slate-200 hover:border-blue-400 rounded-xl transition-all text-left"
          >
            <div class="flex items-center justify-between">
              <div>
                <div class="font-bold text-slate-900">{{ kiosk.name || 'Kiosque sans nom' }}</div>
                <div class="text-xs text-slate-500">{{ kiosk.identifier }}</div>
              </div>
              <div :class="['w-3 h-3 rounded-full', kiosk.online ? 'bg-green-500' : 'bg-red-500']"></div>
            </div>
          </button>
        </div>

        <div class="flex gap-3">
          <button @click="printTicketLocal" class="flex-1 py-3 bg-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-300 transition-all">
            Impression locale
          </button>
          <button @click="showKioskModal = false" class="flex-1 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-700 transition-all">
            Annuler
          </button>
        </div>
      </div>
    </div>

    <!-- Sidebar Droite - Propriétés (reposition ici) -->


    <!-- Sidebar Droite - Propriétés -->
    <aside class="w-80 bg-white border-l border-slate-200 flex flex-col z-20 shadow-xl overflow-hidden">
      <div class="p-6 border-b border-slate-100 bg-blue-50/50">
        <h2 class="font-bold text-slate-800 uppercase text-xs">Propriétés - {{ activeSection }}</h2>
      </div>

      <div class="flex-1 overflow-y-auto p-6 space-y-6">
        <!-- HEADER PROPERTIES -->
        <template v-if="activeSection === 'header'">
          <!-- Logo -->
          <div class="space-y-3">
            <label class="text-[10px] font-black text-slate-400 uppercase">Logo</label>
            <div class="flex items-center gap-2">
              <input type="checkbox" v-model="currentLanguageConfig.header.logo.show" id="showLogo" />
              <label for="showLogo" class="text-xs">Afficher le logo</label>
            </div>

            <div v-if="currentLanguageConfig.header.logo.show" class="space-y-2">
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Logo</label>
                <MediaPicker
                  v-model="currentLanguageConfig.header.logo.url"
                  :agency-id="agencyId"
                  placeholder="Choisir un logo..."
                  button-text="Sélectionner"
                  :show-url-input="true"
                />
              </div>

              <!-- Aperçu du logo sélectionné -->
              <div v-if="currentLanguageConfig.header.logo.url" class="mt-3 p-4 bg-white rounded-xl border-2 border-slate-200">
                <div class="flex items-center gap-3">
                  <img
                    :src="currentLanguageConfig.header.logo.url"
                    alt="Logo preview"
                    class="w-16 h-16 object-contain rounded-lg bg-slate-50 border border-slate-200"
                  />
                  <div class="flex-1">
                    <p class="text-xs font-bold text-slate-700">Logo sélectionné</p>
                    <p class="text-[10px] text-slate-400 truncate max-w-[200px]">
                      {{ currentLanguageConfig.header.logo.url }}
                    </p>
                  </div>
                  <button
                    @click="currentLanguageConfig.header.logo.url = ''"
                    class="text-red-500 hover:bg-red-50 p-2 rounded-lg transition"
                    title="Supprimer le logo"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Largeur (mm)</label>
                <input type="number" v-model.number="currentLanguageConfig.header.logo.width" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Alignement</label>
                <select v-model="currentLanguageConfig.header.logo.align" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                  <option value="left">Gauche</option>
                  <option value="center">Centre</option>
                  <option value="right">Droite</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Header Text -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Texte d'en-tête</label>
            <div class="flex items-center gap-2">
              <input type="checkbox" v-model="currentLanguageConfig.header.text.show" id="showHeaderText" />
              <label for="showHeaderText" class="text-xs">Afficher le texte</label>
            </div>

            <div v-if="currentLanguageConfig.header.text.show" class="space-y-2">
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Contenu</label>
                <input type="text" v-model="currentLanguageConfig.header.text.content" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[10px] text-slate-500 mb-1 block">Taille (pt)</label>
                  <input type="number" v-model.number="currentLanguageConfig.header.text.fontSize" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
                </div>
                <div>
                  <label class="text-[10px] text-slate-500 mb-1 block">Graisse</label>
                  <select v-model="currentLanguageConfig.header.text.fontWeight" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                    <option value="normal">Normal</option>
                    <option value="bold">Gras</option>
                  </select>
                </div>
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Alignement</label>
                <select v-model="currentLanguageConfig.header.text.align" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                  <option value="left">Gauche</option>
                  <option value="center">Centre</option>
                  <option value="right">Droite</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Separator -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Séparateur</label>
            <div class="flex items-center gap-2">
              <input type="checkbox" v-model="currentLanguageConfig.header.separator.show" id="showHeaderSep" />
              <label for="showHeaderSep" class="text-xs">Afficher séparateur</label>
            </div>

            <div v-if="currentLanguageConfig.header.separator.show">
              <label class="text-[10px] text-slate-500 mb-1 block">Style</label>
              <select v-model="currentLanguageConfig.header.separator.style" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                <option value="solid">Solide</option>
                <option value="dashed">Tirets</option>
                <option value="dotted">Pointillés</option>
              </select>
            </div>
          </div>
        </template>

        <!-- BODY PROPERTIES -->
        <template v-if="activeSection === 'body'">
          <!-- Label -->
          <div class="space-y-3">
            <label class="text-[10px] font-black text-slate-400 uppercase">Label</label>
            <div class="flex items-center gap-2">
              <input type="checkbox" v-model="currentLanguageConfig.body.label.show" id="showLabel" />
              <label for="showLabel" class="text-xs">Afficher le label</label>
            </div>

            <div v-if="currentLanguageConfig.body.label.show" class="space-y-2">
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Texte</label>
                <input type="text" v-model="currentLanguageConfig.body.label.text" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Taille (pt)</label>
                <input type="number" v-model.number="currentLanguageConfig.body.label.fontSize" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
            </div>
          </div>

          <!-- Ticket Number -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Numéro de Ticket</label>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Taille (pt)</label>
                <input type="number" v-model.number="currentLanguageConfig.body.ticketNumber.fontSize" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Graisse</label>
                <select v-model="currentLanguageConfig.body.ticketNumber.fontWeight" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                  <option value="normal">Normal</option>
                  <option value="bold">Gras</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Additional Info -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Informations Supplémentaires</label>
            <div class="space-y-2">
              <div v-for="(info, idx) in currentLanguageConfig.body.additionalInfo" :key="idx"
                   class="p-3 bg-slate-50 rounded-lg border">
                <div class="flex justify-between items-center mb-2">
                  <input type="checkbox" v-model="info.show" />
                  <button @click="removeAdditionalInfo(idx)" class="text-red-500 text-xs">Supprimer</button>
                </div>
                <select v-model="info.type" class="w-full p-1 bg-white rounded border text-[10px] mb-1">
                  <option value="date">Date</option>
                  <option value="time">Heure</option>
                  <option value="datetime">Date & Heure</option>
                  <option value="queue">File d'attente</option>
                  <option value="counter">Guichet</option>
                  <option value="custom">Personnalisé</option>
                </select>
                <input v-if="info.type === 'custom'" type="text" v-model="info.customValue" placeholder="Valeur" class="w-full p-1 bg-white rounded border text-[10px]" />
              </div>

              <button @click="addAdditionalInfo" class="w-full py-2 border-2 border-dashed border-slate-200 rounded-lg text-slate-400 text-xs hover:border-blue-400 hover:text-blue-500">
                + Ajouter info
              </button>
            </div>
          </div>

          <!-- QR Code -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">QR Code</label>
            <div class="flex items-center gap-2">
              <input type="checkbox" v-model="currentLanguageConfig.body.qrCode.show" id="showQR" />
              <label for="showQR" class="text-xs">Afficher QR Code</label>
            </div>

            <div v-if="currentLanguageConfig.body.qrCode.show" class="space-y-2">
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Taille (mm)</label>
                <input type="number" v-model.number="currentLanguageConfig.body.qrCode.size" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Données</label>
                <select v-model="currentLanguageConfig.body.qrCode.data" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                  <option value="ticket_id">ID Ticket</option>
                  <option value="ticket_number">Numéro Ticket</option>
                  <option value="custom_url">URL Personnalisée</option>
                </select>
              </div>
            </div>
          </div>
        </template>

        <!-- FOOTER PROPERTIES -->
        <template v-if="activeSection === 'footer'">
          <!-- Footer Text -->
          <div class="space-y-3">
            <label class="text-[10px] font-black text-slate-400 uppercase">Texte de pied</label>
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Contenu</label>
              <textarea v-model="currentLanguageConfig.footer.text.content" rows="2" class="w-full p-2 bg-slate-50 rounded-lg border text-xs"></textarea>
            </div>
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Taille (pt)</label>
              <input type="number" v-model.number="currentLanguageConfig.footer.text.fontSize" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
            </div>
          </div>

          <!-- Contact Info -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Informations de Contact</label>
            <div class="flex items-center gap-2">
              <input type="checkbox" v-model="currentLanguageConfig.footer.contact.show" id="showContact" />
              <label for="showContact" class="text-xs">Afficher contact</label>
            </div>

            <div v-if="currentLanguageConfig.footer.contact.show" class="space-y-2">
              <div v-for="(line, idx) in currentLanguageConfig.footer.contact.lines" :key="idx" class="flex gap-2">
                <input type="text" v-model="currentLanguageConfig.footer.contact.lines[idx]" class="flex-1 p-2 bg-slate-50 rounded-lg border text-xs" />
                <button @click="currentLanguageConfig.footer.contact.lines.splice(idx, 1)" class="text-red-500">✕</button>
              </div>
              <button @click="currentLanguageConfig.footer.contact.lines.push('')" class="w-full py-2 border-2 border-dashed border-slate-200 rounded-lg text-slate-400 text-xs hover:border-blue-400 hover:text-blue-500">
                + Ajouter ligne
              </button>
            </div>
          </div>

          <!-- Separator -->
          <div class="space-y-3 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Séparateur</label>
            <div class="flex items-center gap-2">
              <input type="checkbox" v-model="currentLanguageConfig.footer.separator.show" id="showFooterSep" />
              <label for="showFooterSep" class="text-xs">Afficher séparateur</label>
            </div>

            <div v-if="currentLanguageConfig.footer.separator.show">
              <label class="text-[10px] text-slate-500 mb-1 block">Style</label>
              <select v-model="currentLanguageConfig.footer.separator.style" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                <option value="solid">Solide</option>
                <option value="dashed">Tirets</option>
                <option value="dotted">Pointillés</option>
              </select>
            </div>
          </div>
        </template>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { toast } from "vue3-toastify";
import { useRoute } from 'vue-router';
import LanguageTabs from '@/components/LanguageTabs.vue';
import MediaPicker from '@/components/MediaPicker.vue';
import { io } from 'socket.io-client';

const route = useRoute();
const agencyId = route.params.kioskId;

const activeLanguage = ref('fr');
const activeSection = ref('header');

// WebSocket
const socket = ref(null);

// Kiosk selection
const availableKiosks = ref([]);
const showKioskModal = ref(false);

const availableLanguages = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' }
];

const config = ref({
  agencyId: agencyId,
  enabled: true,
  dimensions: {
    width: 58,
    paperType: '58mm'
  },
  languages: {
    fr: {
      header: {
        show: true,
        logo: { show: false, url: '', width: 40, align: 'center' },
        text: { show: true, content: 'Bienvenue', fontSize: 16, fontWeight: 'bold', align: 'center', marginTop: 5, marginBottom: 5 },
        separator: { show: true, style: 'dashed' }
      },
      body: {
        label: { show: true, text: 'Votre numéro', fontSize: 12, align: 'center' },
        ticketNumber: { fontSize: 48, fontWeight: 'bold', align: 'center', marginTop: 8, marginBottom: 8 },
        additionalInfo: [
          { show: true, type: 'datetime', label: 'Date', fontSize: 10, align: 'center', order: 0 }
        ],
        qrCode: { show: false, size: 25, data: 'ticket_id', align: 'center', marginTop: 5 }
      },
      footer: {
        show: true,
        separator: { show: true, style: 'dashed' },
        text: { content: 'Merci de votre visite', fontSize: 10, align: 'center', marginTop: 5, marginBottom: 5 },
        contact: { show: false, lines: [], fontSize: 8, align: 'center' }
      },
      margins: { top: 5, bottom: 5, left: 3, right: 3 }
    }
  }
});

const ticketWidth = computed(() => {
  return config.value.dimensions.paperType === '58mm' ? 58 : 80;
});

const configuredLanguages = computed(() => {
  return Object.keys(config.value.languages || {});
});

const currentLanguageConfig = computed(() => {
  // Si la langue n'existe pas encore, on renvoie le template FR 
  // mais SANS modifier config.value ici.
  return config.value.languages[activeLanguage.value] || config.value.languages.fr;
});

// 2. On utilise un Watcher pour initialiser la langue si elle manque
// Cela déplace l'effet de bord en dehors du cycle de rendu du computed
watch(activeLanguage, (newLang) => {
  if (!config.value.languages[newLang]) {
    // On initialise la nouvelle langue avec une copie profonde de la config FR
    config.value.languages[newLang] = JSON.parse(JSON.stringify(config.value.languages.fr));
    toast.info(`Initialisation de la langue : ${newLang.toUpperCase()}`);
  }
}, { immediate: true });

// 3. Modifie la fonction sortedAdditionalInfo pour être plus robuste
const sortedAdditionalInfo = computed(() => {
  const info = currentLanguageConfig.value?.body?.additionalInfo || [];
  return [...info].sort((a, b) => (a.order || 0) - (b.order || 0));
});

const copyLanguageConfig = ({ from, to }) => {
  if (config.value.languages[from]) {
    config.value.languages[to] = JSON.parse(JSON.stringify(config.value.languages[from]));
    toast.success(`Configuration copiée de ${from} vers ${to}`);
  }
};

const addAdditionalInfo = () => {
  currentLanguageConfig.value.body.additionalInfo.push({
    show: true,
    type: 'custom',
    label: 'Info',
    customValue: '',
    fontSize: 10,
    align: 'center',
    order: currentLanguageConfig.value.body.additionalInfo.length
  });
};

const removeAdditionalInfo = (idx) => {
  currentLanguageConfig.value.body.additionalInfo.splice(idx, 1);
};

const fetchKiosks = async () => {
  try {
    const token = localStorage.getItem("superAdminToken");
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/kiosks`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    if (!res.ok) throw new Error("Erreur récupération kiosques");
    const allKiosks = await res.json();

    // Filtrer uniquement les kiosques de cette agence
    const agencyKiosks = allKiosks.filter(k => k.agencyId?.toString() === agencyId);
    availableKiosks.value = agencyKiosks;
    return availableKiosks.value;
  } catch (err) {
    console.error("Erreur fetchKiosks:", err);
    toast.error("Erreur lors de la récupération des kiosques");
    return [];
  }
};

const printTicket = async () => {
  // Vérifier si l'agence a des kiosques
  const kiosks = await fetchKiosks();

  if (kiosks.length > 0) {
    // Afficher le modal de sélection de kiosque
    showKioskModal.value = true;
  } else {
    // Aucun kiosque, impression locale directe
    printTicketLocal();
  }
};

const printTicketLocal = () => {
  showKioskModal.value = false;
  const printWindow = window.open('', '_blank');
  const ticketContent = document.querySelector('.ticket-content').innerHTML;
  printWindow.document.write(`
    <html>
      <head>
        <title>Ticket Preview</title>
        <style>
          @page { size: ${ticketWidth.value}mm auto; margin: 0; }
          body { margin: 0; padding: 0; font-family: monospace; }
          .ticket-content { width: ${ticketWidth.value}mm; }
        </style>
      </head>
      <body>${ticketContent}</body>
    </html>
  `);
  printWindow.document.close();
  printWindow.print();
};

const selectKiosk = async (kiosk) => {
  showKioskModal.value = false;

  if (!socket.value || !socket.value.connected) {
    toast.error("WebSocket non connecté. Reconnexion...");
    initWebSocket();
    setTimeout(() => selectKiosk(kiosk), 2000);
    return;
  }

  const idToast = toast.loading(`Envoi de l'impression au kiosque ${kiosk.name}...`);

  try {
    const testData = {
      number: 'A042',
      queueName: 'Service Test',
      date: new Date().toISOString(),
      time: new Date().toLocaleTimeString()
    };

    console.log(`[Print Test] 🖨️ Envoi test d'impression via WebSocket au kiosque ${kiosk._id}`);

    // Écouter la réponse (une seule fois)
    socket.value.once("print_test_result", (result) => {
      console.log("[Print Test] 📥 Résultat reçu:", result);

      if (result.success) {
        toast.update(idToast, {
          render: `✅ ${result.message || 'Impression envoyée avec succès'}`,
          type: "success",
          isLoading: false,
          autoClose: 3000
        });

        if (!result.kioskOnline) {
          setTimeout(() => {
            toast.warning(`⚠️ Attention : Le kiosque ${kiosk.name} est hors ligne`);
          }, 500);
        }
      } else {
        toast.update(idToast, {
          render: `❌ ${result.error || 'Erreur inconnue'}`,
          type: "error",
          isLoading: false,
          autoClose: 3000
        });
      }
    });

    // Envoyer via WebSocket
    socket.value.emit("print_test_ticket", {
      kioskId: kiosk.id,
      ticketConfig: config.value,
      testData: testData
    });

    // Timeout si pas de réponse après 10 secondes
    setTimeout(() => {
      if (idToast) {
        toast.update(idToast, {
          render: "⏱️ Pas de réponse du serveur. Le kiosque a peut-être reçu la commande.",
          type: "warning",
          isLoading: false,
          autoClose: 3000
        });
      }
    }, 10000);

  } catch (err) {
    console.error("Erreur selectKiosk:", err);
    toast.update(idToast, {
      render: `❌ ${err.message}`,
      type: "error",
      isLoading: false,
      autoClose: 3000
    });
  }
};


const fetchConfig = async () => {
  try {
    const token = localStorage.getItem("superAdminToken");
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/ticket-config/${agencyId}`, {
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    });
    if (res.status === 404) {
      return;
    }
    const data = await res.json();
    config.value = data;
  } catch (err) {
    toast.error("Erreur chargement config : " + err.message);
  }
};

const saveConfig = async () => {
  const idToast = toast.loading("Enregistrement...");
  try {
    const token = localStorage.getItem("superAdminToken");
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/ticket-config/${agencyId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(config.value)
    });
    if (!res.ok) throw new Error("Erreur sauvegarde");

    toast.update(idToast, { render: "🚀 Configuration publiée !", type: "success", isLoading: false, autoClose: 3000 });

    // La mise à jour sera automatiquement envoyée aux kiosques via WebSocket
    // par le backend (voir ticketConfig.js ligne 45)
    console.log("[Config] ✅ Configuration sauvegardée, broadcast automatique aux kiosques");

  } catch (err) {
    toast.update(idToast, { render: err.message, type: "error", isLoading: false, autoClose: 3000 });
  }
};

const initWebSocket = () => {
  const token = localStorage.getItem("superAdminToken");

  if (!token) {
    console.error("[WebSocket] Token manquant");
    toast.error("Token d'authentification manquant");
    return;
  }

  console.log("[WebSocket] 🔌 Connexion au serveur...");

  socket.value = io("https://api.datafuse.fr", {
    query: { token },
    transports: ['websocket', 'polling']
  });

  socket.value.on("connect", () => {
    console.log("[WebSocket] ✅ Connecté au serveur");
    toast.success("WebSocket connecté", { autoClose: 2000 });
  });

  socket.value.on("disconnect", (reason) => {
    console.warn("[WebSocket] ⚠️ Déconnecté:", reason);
    toast.warning("WebSocket déconnecté");
  });

  socket.value.on("connect_error", (err) => {
    console.error("[WebSocket] ❌ Erreur de connexion:", err);
    toast.error("Erreur de connexion WebSocket");
  });

  // Écouter les mises à jour de config (confirmation après sauvegarde)
  socket.value.on("ticket_config_update", (data) => {
    console.log("[WebSocket] 📥 Mise à jour config ticket reçue:", data);
  });

  // Écouter la réponse après sauvegarde via WebSocket
  socket.value.on("update_ticket_config_result", (result) => {
    console.log("[WebSocket] 📥 Résultat sauvegarde config:", result);
    if (result.success) {
      console.log("✅ Config broadcast aux kiosques");
    }
  });
};

const disconnectWebSocket = () => {
  if (socket.value) {
    console.log("[WebSocket] 🔌 Déconnexion...");
    socket.value.disconnect();
    socket.value = null;
  }
};

// === EXPORT/IMPORT ===
const exportConfig = () => {
  const dataStr = JSON.stringify(config.value, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `ticket-design-${Date.now()}.json`;
  link.click();
  URL.revokeObjectURL(url);
  toast.success("Design exporté!");
};

const importConfig = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const imported = JSON.parse(e.target.result);

      // Conserver l'agencyId actuel
      imported.agencyId = config.value.agencyId;

      // Charger la configuration importée
      config.value = imported;

      toast.success("Design importé!");

      // Réinitialiser l'input file pour permettre de réimporter le même fichier
      event.target.value = '';
    } catch (err) {
      toast.error("Fichier invalide");
      event.target.value = '';
    }
  };
  reader.readAsText(file);
};

onMounted(() => {
  fetchConfig();
  initWebSocket();
});

onUnmounted(() => {
  disconnectWebSocket();
});
</script>

<style scoped>
.font-sans { font-family: 'Plus Jakarta Sans', sans-serif; }
.ticket-content {
  min-height: 100mm;
  background: white;
}
</style>
